import React, { useState, useEffect } from 'react';
import { useRealTimeData } from '../contexts/RealTimeDataContext';
import { TrendingUp, TrendingDown, Wifi, WifiOff, Clock, DollarSign, Users, Activity } from 'lucide-react';

const RealTimeDataDisplay: React.FC = () => {
  const { data, isConnected, lastUpdate } = useRealTimeData();
  const [isVisible, setIsVisible] = useState(true);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    return `${Math.floor(diffInSeconds / 3600)}h ago`;
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current > previous) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (current < previous) return <TrendingDown className="w-4 h-4 text-red-400" />;
    return null;
  };

  const getTrendColor = (current: number, previous: number) => {
    if (current > previous) return 'text-green-400';
    if (current < previous) return 'text-red-400';
    return 'text-slate-400';
  };

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 bg-slate-800 border border-slate-700 rounded-lg p-3 hover:bg-slate-700 transition-colors"
      >
        <Activity className="w-5 h-5 text-cyan-400" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-slate-800/95 backdrop-blur-sm border border-slate-700 rounded-xl shadow-2xl max-w-sm">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isConnected ? (
              <Wifi className="w-4 h-4 text-green-400" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-400" />
            )}
            <span className="text-white font-medium">Live Data</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-slate-400 text-xs">
              {formatTimeAgo(lastUpdate)}
            </span>
            <button
              onClick={() => setIsVisible(false)}
              className="text-slate-400 hover:text-white transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div className="p-4 space-y-4">
        {/* Treasury */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Treasury Value</span>
            <div className="flex items-center space-x-1">
              {getTrendIcon(data.treasury.totalValue, 7.2)}
              <span className={`text-sm font-medium ${getTrendColor(data.treasury.totalValue, 7.2)}`}>
                {data.treasury.totalValue.toFixed(1)}M SUI
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Daily Yield</span>
            <span className="text-white text-sm font-medium">
              {formatCurrency(data.treasury.dailyYield)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">APY</span>
            <span className="text-cyan-400 text-sm font-medium">
              {data.treasury.apy.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* Members */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Total Members</span>
            <div className="flex items-center space-x-1">
              {getTrendIcon(data.members.totalMembers, 247)}
              <span className={`text-sm font-medium ${getTrendColor(data.members.totalMembers, 247)}`}>
                {data.members.totalMembers}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Active Today</span>
            <span className="text-white text-sm font-medium">
              {data.members.activeMembers}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">New Members</span>
            <span className="text-green-400 text-sm font-medium">
              +{data.members.newMembersToday}
            </span>
          </div>
        </div>

        {/* Horses */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Racing Today</span>
            <span className="text-white text-sm font-medium">
              {data.horses.racingToday} horses
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Total Winnings</span>
            <span className="text-green-400 text-sm font-medium">
              {formatCurrency(data.horses.totalWinnings)}
            </span>
          </div>
        </div>

        {/* Governance */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Active Proposals</span>
            <span className="text-cyan-400 text-sm font-medium">
              {data.governance.activeProposals}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Participation</span>
            <span className="text-white text-sm font-medium">
              {data.governance.participationRate.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* System Status */}
        <div className="pt-2 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">System Uptime</span>
            <span className="text-green-400 text-sm font-medium">
              {data.system.uptime.toFixed(2)}%
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Security Score</span>
            <span className="text-green-400 text-sm font-medium">
              {data.system.securityScore.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-700 bg-slate-700/30">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-2">
            <Clock className="w-3 h-3 text-slate-400" />
            <span className="text-slate-400">Last update: {lastUpdate.toLocaleTimeString()}</span>
          </div>
          <div className={`flex items-center space-x-1 ${
            isConnected ? 'text-green-400' : 'text-red-400'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-400' : 'bg-red-400'
            }`}></div>
            <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeDataDisplay;
